import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../components/button/Button';
import { Card } from '../components/card/Card';
import { PageShell } from '../components/PageShell';
import { KillRequestOverlay } from '../components/KillRequestOverlay';
import { WitnessReportForm } from '../components/witness/WitnessReportForm';
import { WitnessNotification } from '../components/witness/WitnessNotification';
import { ModIncidentPanel } from '../components/witness/ModIncidentPanel';
import { SpectatorView } from '../components/SpectatorView';
import { BloodSplash } from '../components/BloodSplash';
import { LiveEventFeed } from '../components/LiveEventFeed';
import { WeaponPoolCard } from '../components/weaponPool/WeaponPoolCard';
import { WeaponTemplatePickerModal } from '../components/weaponPool/WeaponTemplatePickerModal';
import { PodiumCard } from '../components/podium/PodiumCard';

import { loadSession, clearSession } from '../api/session';
import { useGameEvents } from '../api/useGameEvents';
import { useLanguage } from '../api/useLanguage';
import {
  requestKill,
  leaveGame,
  cancelGame,
  startGame,
  restartGame,
  kickPlayer,
  fetchWeapons,
  addWeapon,
  suggestWeapon,
  moderateWeapon,
  removeWeapon,
  fetchPodium,
  type WeaponResponse,
  type PodiumResponse,
} from '../api/games';
import { PlayerPoolCard } from '../components/playerPool/PlayerPoolCard';
import { RulesDropdown } from '../components/rules/RulesDropdown';
import styles from './PlayPage.module.css';

import knifeImg from '../assets/knife.png';
import axeImg from '../assets/axe.png';

export default function PlayPage() {
  const { code = '' } = useParams();
  const nav = useNavigate();

  const session = useMemo(() => loadSession(code), [code]);
  const {
    game,
    myCurrentTarget,
    myCurrentWeapon,
    pendingKill,
    killAccepted,
    killDeclined,
    killCount,
    dismissKillAccepted,
    dismissKillDeclined,
    acceptPendingKill,
    declinePendingKill,
    weaponEventCount,
    pendingWitnessReport,
    wasArrested,
    dismissArrested,
    pendingModReports,
    liveEvents,
    killRequestCount,
    refreshGame,
  } = useGameEvents(session);

  const [killRequestPending, setKillRequestPending] = useState(false);
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [podium, setPodium] = useState<PodiumResponse | null>(null);
  const [restartWeaponMode, setRestartWeaponMode] = useState(false);
  const [copied, setCopied] = useState(false);
  const [lang, setLang] = useLanguage();

  // Moderator only weapon pool
  const [weapons, setWeapons] = useState<WeaponResponse[]>([]);
  const [newWeaponName, setNewWeaponName] = useState('');
  const [newWeaponDesc, setNewWeaponDesc] = useState('');

  // Player suggest form state
  const [suggestName, setSuggestName] = useState('');
  const [suggestDesc, setSuggestDesc] = useState('');
  const [suggested, setSuggested] = useState(false);

  const isModerator = session?.playerName === game?.moderator;

  const onGoHome = () => {
    if (session) clearSession(session.code);
    nav('/');
  };

  const refreshWeapons = useCallback(async () => {
    if (!session || !isModerator || !game?.weapon_mode) return;
    try {
      setWeapons(await fetchWeapons(code, session.playerName));
    } catch {
      /* ignore */
    }
  }, [code, session, isModerator, game?.weapon_mode]);

  useEffect(() => {
    refreshWeapons();
  }, [weaponEventCount, refreshWeapons]);

  useEffect(() => {
    if (killAccepted || killDeclined) setKillRequestPending(false);
  }, [killAccepted, killDeclined]);

  useEffect(() => {
    if (game?.state === 'finished') setRestartWeaponMode(game.weapon_mode);
  }, [game?.state, game?.weapon_mode]);

  useEffect(() => {
    if (game?.state !== 'finished') return;
    fetchPodium(code)
      .then(setPodium)
      .catch(() => {});
  }, [code, game?.state]);

  if (!session) {
    return (
      <PageShell>
        <Card>
          <div className="col">
            <p>Session not found.</p>
            <Button onClick={() => nav('/join')}>Go to join</Button>
          </div>
        </Card>
      </PageShell>
    );
  }

  if (!game) {
    return (
      <PageShell>
        <Card>
          <p>Connecting…</p>
        </Card>
      </PageShell>
    );
  }

  const myMember = game.members.find((m) => m.name === session.playerName);
  const myStatus = myMember?.status;
  const isSpectating =
    !isModerator &&
    game.state === 'started' &&
    (myStatus === 'dead' || myStatus === 'arrested' || (!myMember && session !== null));

  if (isSpectating) {
    const spectatorStatus = myStatus === 'arrested' ? 'arrested' : myMember ? 'dead' : 'left';
    const activePlayers = game.members.filter((m) => m.status === 'active').length;
    return (
      <PageShell>
        <BloodSplash triggerCount={killRequestCount} />
        <SpectatorView
          status={spectatorStatus}
          activePlayers={activePlayers}
          liveEvents={liveEvents}
          gameFinished={game.state === 'finished'}
          onGoHome={onGoHome}
        />
      </PageShell>
    );
  }

  const onCancelGame = async () => {
    const confirmed = window.confirm(
      'Cancel the game? All players will be removed and the game will be deleted.'
    );
    if (!confirmed) return;
    try {
      await cancelGame(session.code, session.playerName);
    } catch {
      /* ignore */
    }
    clearSession(session.code);
    nav('/');
  };

  const onLeave = async () => {
    const confirmed = window.confirm(
      'Leave the game? You will become a spectator and can watch until the game ends.'
    );
    if (!confirmed) return;
    try {
      await leaveGame(session.code, session.playerName, session.token);
    } catch {
      /* ignore */
    }
    await refreshGame();
  };

  // Lobby

  if (game.state === 'lobby') {
    const activeMembers = game.members.filter((m) => m.status === 'active');
    const approvedCount = weapons.filter((w) => w.status === 'approved').length;
    const enoughWeapons = !game.weapon_mode || approvedCount >= activeMembers.length;

    const onShareCode = async () => {
      await navigator.clipboard.writeText(game.join_code).catch(() => {});
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    };

    const onKick = async (playerName: string) => {
      try {
        await kickPlayer(session.code, session.playerName, playerName);
      } catch (e: unknown) {
        alert(e instanceof Error ? e.message : 'Could not kick player');
      }
    };

    const onStart = async () => {
      try {
        await startGame(session.code, session.playerName);
      } catch (e: unknown) {
        alert(e instanceof Error ? e.message : 'Could not start game');
      }
    };

    const onApprove = (id: number) =>
      moderateWeapon(code, session.playerName, id, 'approve')
        .then(refreshWeapons)
        .catch(() => {});
    const onDecline = (id: number) =>
      moderateWeapon(code, session.playerName, id, 'decline')
        .then(refreshWeapons)
        .catch(() => {});
    const onRemove = (id: number) =>
      removeWeapon(code, session.playerName, id)
        .then(refreshWeapons)
        .catch(() => {});

    const onAddWeapon = async (e: React.FormEvent) => {
      e.preventDefault();
      if (!newWeaponName.trim()) return;
      try {
        await addWeapon(code, session.playerName, newWeaponName.trim(), newWeaponDesc.trim());
        setNewWeaponName('');
        setNewWeaponDesc('');
        refreshWeapons();
      } catch (err) {
        alert(err instanceof Error ? err.message : 'Failed to add weapon');
      }
    };

    const onSuggest = async (e: React.FormEvent) => {
      e.preventDefault();
      if (!suggestName.trim()) return;
      try {
        await suggestWeapon(
          code,
          session.playerName,
          session.token,
          suggestName.trim(),
          suggestDesc.trim()
        );
        setSuggestName('');
        setSuggestDesc('');
        setSuggested(true);
      } catch (err) {
        alert(err instanceof Error ? err.message : 'Failed to suggest weapon');
      }
    };

    return (
      <PageShell>
        {showTemplatePicker && (
          <WeaponTemplatePickerModal
            code={code}
            moderatorName={session.playerName}
            lang={lang}
            onClose={() => setShowTemplatePicker(false)}
            onImported={() => {
              setShowTemplatePicker(false);
              refreshWeapons();
            }}
          />
        )}

        <RulesDropdown lang={lang} onLangChange={setLang} />

        <Card title="Join Code" subtitle="Share this with other players">
          <div className={styles.codeRow}>
            <p className={styles.code}>{game.join_code}</p>
            <button type="button" className={styles.copyBtn} onClick={onShareCode}>
              {copied ? '✓' : 'Copy'}
            </button>
          </div>
        </Card>

        <Card title={`Players · ${activeMembers.length}`}>
          <PlayerPoolCard members={activeMembers} isModerator={isModerator} onKick={onKick} />
        </Card>

        {isModerator && game.weapon_mode && (
          <Card title="Weapon pool">
            <div className="col">
              <WeaponPoolCard
                weapons={weapons}
                isModerator
                onApprove={onApprove}
                onDecline={onDecline}
                onRemove={onRemove}
              />

              <form onSubmit={onAddWeapon} className={styles.weaponForm}>
                <p className={styles.formLabel}>Add custom weapon</p>
                <input
                  className={styles.input}
                  placeholder="Weapon name"
                  value={newWeaponName}
                  onChange={(e) => setNewWeaponName(e.target.value)}
                  required
                />
                <input
                  className={styles.input}
                  placeholder="How to use it (optional)"
                  value={newWeaponDesc}
                  onChange={(e) => setNewWeaponDesc(e.target.value)}
                />
                <Button type="submit" disabled={!newWeaponName.trim()}>
                  Add weapon
                </Button>
              </form>

              <Button variant="secondary" onClick={() => setShowTemplatePicker(true)}>
                Browse weapon templates
              </Button>
            </div>
          </Card>
        )}

        {!isModerator && game.weapon_mode && (
          <Card title="Suggest a weapon">
            {suggested ? (
              <div className="col">
                <p className={styles.suggestDone}>Suggestion sent! The host will review it.</p>
                <Button variant="secondary" onClick={() => setSuggested(false)}>
                  Suggest another
                </Button>
              </div>
            ) : (
              <form onSubmit={onSuggest} className={styles.weaponForm}>
                <input
                  className={styles.input}
                  placeholder="Weapon name"
                  value={suggestName}
                  onChange={(e) => setSuggestName(e.target.value)}
                  required
                />
                <input
                  className={styles.input}
                  placeholder="How to use it (optional)"
                  value={suggestDesc}
                  onChange={(e) => setSuggestDesc(e.target.value)}
                />
                <Button type="submit" disabled={!suggestName.trim()}>
                  Suggest
                </Button>
              </form>
            )}
          </Card>
        )}

        <Card>
          <div className="col">
            {isModerator ? (
              <>
                {game.weapon_mode && !enoughWeapons && (
                  <p className={styles.weaponHint}>
                    Need {activeMembers.length - approvedCount} more approved weapon
                    {activeMembers.length - approvedCount !== 1 ? 's' : ''} to start
                  </p>
                )}
                <Button onClick={onStart} disabled={activeMembers.length < 2 || !enoughWeapons}>
                  Start Game
                </Button>
                <Button variant="secondary" onClick={onCancelGame}>
                  Cancel game
                </Button>
              </>
            ) : (
              <>
                <p>Waiting for host to start…</p>
                <Button variant="secondary" onClick={onLeave}>
                  Leave
                </Button>
              </>
            )}
          </div>
        </Card>
      </PageShell>
    );
  }

  // Started

  if (game.state === 'started' && isModerator) {
    const activePlayers = game.members.filter((m) => m.status === 'active');
    const deadPlayers = game.members.filter((m) => m.status === 'dead');

    const onKickActive = async (playerName: string) => {
      try {
        await kickPlayer(session.code, session.playerName, playerName);
      } catch (e: unknown) {
        alert(e instanceof Error ? e.message : 'Could not kick player');
      }
    };

    const arrestedPlayers = game.members.filter((m) => m.status === 'arrested');

    return (
      <PageShell>
        <Card title="Game in progress" subtitle={`${activePlayers.length} players remaining`}>
          <div className="col">
            {pendingModReports.length > 0 && (
              <ModIncidentPanel
                code={code}
                moderatorName={session.playerName}
                reports={pendingModReports}
              />
            )}
            <LiveEventFeed events={liveEvents} />
            {activePlayers.length > 0 && (
              <>
                <p className={styles.sectionLabel}>Active</p>
                <PlayerPoolCard members={activePlayers} isModerator onKick={onKickActive} />
              </>
            )}
            {arrestedPlayers.length > 0 && (
              <>
                <p className={styles.sectionLabel}>Arrested</p>
                <ul className={styles.playerList}>
                  {arrestedPlayers.map((m) => (
                    <li key={m.name} className={styles.dead}>
                      {m.name}
                    </li>
                  ))}
                </ul>
              </>
            )}
            {deadPlayers.length > 0 && (
              <>
                <p className={styles.sectionLabel}>Eliminated</p>
                <ul className={styles.playerList}>
                  {deadPlayers.map((m) => (
                    <li key={m.name} className={styles.dead}>
                      {m.name}
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        </Card>
      </PageShell>
    );
  }

  if (game.state === 'started') {
    const onKill = async () => {
      try {
        await requestKill(session.code, session.playerName, session.token);
        setKillRequestPending(true);
      } catch (e: unknown) {
        alert(e instanceof Error ? e.message : 'Kill request failed');
      }
    };

    const activePlayerNames = game.members.filter((m) => m.status === 'active').map((m) => m.name);

    return (
      <PageShell>
        <BloodSplash triggerCount={killRequestCount} />

        {pendingKill && (
          <KillRequestOverlay
            killerName={pendingKill.killerName}
            onAccept={() => void acceptPendingKill()}
            onDecline={() => void declinePendingKill()}
          />
        )}

        {pendingWitnessReport && (
          <WitnessNotification code={code} token={session.token} report={pendingWitnessReport} />
        )}

        {killCount > 0 && (
          <div className={styles.axeTracker}>
            {Array.from({ length: killCount }).map((_, i) => (
              <img key={i} src={axeImg} className={styles.axeIcon} alt="kill" />
            ))}
          </div>
        )}

        <Card
          title={session.playerName}
          subtitle={myCurrentTarget ? `Target: ${myCurrentTarget}` : 'Target unknown'}
        >
          <div className="col">
            {game.weapon_mode && myCurrentWeapon && (
              <div className={styles.weaponCard}>
                <p className={styles.weaponTitle}>Your weapon: {myCurrentWeapon.name}</p>
                <p className={styles.weaponDesc}>{myCurrentWeapon.description}</p>
              </div>
            )}
            {killRequestPending ? (
              <div className={styles.knifeWrapper}>
                <img src={knifeImg} className={styles.knifeSpinning} alt="" />
                <p>Waiting for confirmation…</p>
              </div>
            ) : (
              <Button onClick={onKill} disabled={!myCurrentTarget}>
                Kill
              </Button>
            )}
            <Button variant="secondary" onClick={onLeave}>
              Leave
            </Button>
          </div>
        </Card>

        {killAccepted && (
          <Card title="Kill confirmed! ☠">
            <div className="col">
              <p>Your target confirmed the elimination. You have a new target.</p>
              {game.weapon_mode && myCurrentWeapon && (
                <p className={styles.weaponAssigned}>
                  New weapon: <strong>{myCurrentWeapon.name}</strong>
                </p>
              )}
              <Button onClick={dismissKillAccepted}>Got it</Button>
            </div>
          </Card>
        )}

        {killDeclined && (
          <Card title="Kill declined">
            <div className="col">
              <p>Your target denied the elimination.</p>
              <Button onClick={dismissKillDeclined}>Ok</Button>
            </div>
          </Card>
        )}

        {wasArrested && (
          <Card title="You have been arrested">
            <div className="col">
              <p>A witness report was upheld. You are out of the game as a spectator.</p>
              <Button onClick={dismissArrested}>Got it</Button>
            </div>
          </Card>
        )}

        {game.weapon_mode && (
          <WitnessReportForm
            code={code}
            token={session.token}
            playerNames={activePlayerNames}
            myName={session.playerName}
          />
        )}
      </PageShell>
    );
  }

  // Finished

  const onRestart = async () => {
    try {
      await restartGame(session.code, session.playerName, restartWeaponMode);
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : 'Could not restart game');
    }
  };

  return (
    <PageShell>
      <Card title="Game over">
        <div className="col">
          {podium ? (
            <PodiumCard podium={podium} weaponMode={game.weapon_mode} />
          ) : (
            <>
              {game.winner ? (
                <p className={styles.winner}>
                  Winner: <strong>{game.winner}</strong>
                </p>
              ) : (
                <p>No winner.</p>
              )}
              <p className={styles.loadingStats}>Loading stats…</p>
            </>
          )}
          {isModerator && (
            <div className={styles.restartRow}>
              <label className={styles.restartLabel}>
                <input
                  type="checkbox"
                  checked={restartWeaponMode}
                  onChange={(e) => setRestartWeaponMode(e.target.checked)}
                />
                Weapon mode
              </label>
              <Button onClick={onRestart}>Play again</Button>
            </div>
          )}
          <Button variant="secondary" onClick={onGoHome}>
            Home
          </Button>
        </div>
      </Card>
    </PageShell>
  );
}
